/**
 * TEMPLATE — Copier ce fichier pour créer un nouveau panel.
 * Supprimer les variantes non utilisées et renommer.
 *
 *   Variante A : Pull (createToolQuery)        — données statiques ou
 *                                                  rafraîchissement manuel
 *   Variante B : Push (usePanelSubscription)   — données temps réel,
 *                                                  agent pousse toutes les N ms
 *
 * Les deux variantes utilisent les **primitives partagées** introduites au
 * Lot 2 du plan UI/UX (cf. `shared/panel-primitives.tsx`) :
 *   - <PanelKpi> / <PanelKpiGrid>   — cartes de synthèse en haut du panel
 *   - <PanelBadge intent="…">       — pastilles d'état (intent typé Intent)
 *   - <PanelDetail …>               — drawer latéral (responsive sous 900 px)
 *   - <RefreshIndicator state={…}>  — pastille live/polling/stale/error
 * et le hook `usePanelRefresh` qui synthétise l'état de fraîcheur à partir
 * d'une query TanStack, d'une subscription WS, ou d'un liveSignal custom.
 *
 * Les actions destructives passent **toujours** par `usePanelAction` ou
 * `useConfirm()` — jamais `window.confirm()`. Le relay rejette de toute
 * façon les outils destructifs sans `userConfirmedAt` dans les params.
 */
import { For, Show, createMemo, createSignal, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel, usePanelAction } from "@/shared/use-panel";
import { usePanelSubscription } from "@/shared/use-panel-subscription";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import {
  PanelBadge,
  PanelDetail,
  PanelKpi,
  PanelKpiGrid,
  RefreshIndicator,
  type DetailSection,
  type Intent,
} from "@/shared/panel-primitives";
import { useJobEvents } from "@/shared/use-job-events";

// CUSTOMIZE: type des données retournées par le tool
interface DataRow {
  id?: string;
  name?: string;
  status?: string;
}

interface ListResult {
  rows: DataRow[];
  count?: number;
}

// CUSTOMIZE: mapping statut → Intent pour <PanelBadge>.
function statusIntent(status: string | undefined): Intent {
  if (status === "ok" || status === "Running") return "ok";
  if (status === "warn" || status === "pending") return "warn";
  if (status === "error" || status === "danger" || status === "Stopped") return "danger";
  return "muted";
}

// ──────────────────────────────────────────────────────────────────────────────
// VARIANTE A — Pull (TanStack query, refetch manuel)
// ──────────────────────────────────────────────────────────────────────────────
const TemplatePullPanel: Component = () => {
  const [filter, setFilter] = createSignal("");
  const [selected, setSelected] = createSignal<DataRow | null>(null);
  const [activeJobId, setActiveJobId] = createSignal<string | null>(null);

  // CUSTOMIZE: outil et paramètres
  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "xxx_list",
    params: { filter: filter() },
    enabled: panel.enabled(),
    refetchInterval: 0,
  }));

  const panel = usePanel(() => listQuery);
  const runAction = usePanelAction(panel.setActionStatus, listQuery.refetch);
  const jobState = useJobEvents(() => activeJobId());

  // RefreshIndicator alimenté par la query TanStack — passe automatiquement
  // en "error" si la WS globale tombe (cf. usePanelRefresh).
  const refresh = usePanelRefresh({
    query: () => ({
      isFetching: listQuery.isFetching,
      refetch: () => listQuery.refetch(),
      dataUpdatedAt: listQuery.dataUpdatedAt,
      error: listQuery.error,
    }),
  });

  const rows = createMemo(() => {
    const q = filter().trim().toLowerCase();
    return (listQuery.data?.rows ?? []).filter((r) => !q || (r.name ?? "").toLowerCase().includes(q));
  });

  // CUSTOMIZE: KPI calculés à partir des données déjà chargées.
  const kpis = createMemo(() => {
    const all = listQuery.data?.rows ?? [];
    return { total: all.length, ok: all.filter((r) => r.status === "ok").length };
  });

  // CUSTOMIZE: colonnes. <PanelBadge> pour la colonne État.
  const columns: Column<DataRow>[] = [
    { accessorKey: "name", header: "Nom" },
    {
      accessorKey: "status",
      header: "État",
      cell: (info) => {
        const s = String(info.getValue() ?? "");
        return <PanelBadge intent={statusIntent(s)}>{s || "—"}</PanelBadge>;
      },
    },
  ];

  const toolbar = (
    <>
      <input
        class="panel-filter-input"
        placeholder="Filtrer..."
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <button class="btn btn--xs btn--ghost" onClick={() => listQuery.refetch()}>
        ↺
      </button>
      <RefreshIndicator state={refresh} />
    </>
  );

  // CUSTOMIZE: sections du drawer détail. Chaque DetailSection rend soit un
  // `dl` automatique via `items`, soit un contenu libre via `children`.
  const detailSections = (row: DataRow): DetailSection[] => {
    const out: DetailSection[] = [
      {
        label: "Identité",
        items: [
          ["Nom", <>{row.name ?? "—"}</>],
          ["État", <PanelBadge intent={statusIntent(row.status)}>{row.status ?? "—"}</PanelBadge>],
        ],
      },
    ];
    const js = jobState();
    if (js) {
      out.push({
        label: `Journal (${js.status})`,
        children: (
          <pre class="panel-job-log">
            <For each={js.logs}>{(line) => <div class="panel-job-log-line">{line}</div>}</For>
          </pre>
        ),
      });
    }
    return out;
  };

  return (
    <PanelLayout title="Mon Panel" toolbar={toolbar} status={panel.status()}>
      <PanelKpiGrid>
        <PanelKpi label="Total" value={kpis().total} />
        <PanelKpi label="OK" value={kpis().ok} intent="ok" />
      </PanelKpiGrid>

      <div class="panel-split">
        <div class="panel-split-main">
          <DataTable<DataRow>
            rows={rows()}
            columns={columns}
            rowActions={(r) => (
              <button class="btn btn--xs btn--ghost" onClick={() => setSelected(r)}>
                Détails
              </button>
            )}
          />
        </div>
        <Show when={selected()}>
          {(row) => (
            <PanelDetail
              title={row().name ?? "Détail"}
              onClose={() => setSelected(null)}
              actions={
                <button
                  class="btn btn--xs btn--danger"
                  onClick={async () => {
                    // useConfirm est déclenché en interne par usePanelAction
                    // — pas besoin de window.confirm. Le relay impose
                    // userConfirmedAt pour les outils destructifs.
                    const res = await runAction(
                      "xxx_action",
                      { name: row().name, action: "stop" },
                      {
                        confirmTitle: `Arrêter ${row().name}`,
                        confirmBody: "Confirmer l'arrêt ?",
                        level: "danger",
                        entityName: row().name,
                      },
                    );
                    if (res && typeof res === "object" && "jobId" in res) {
                      const job = res as { jobId?: unknown };
                      if (typeof job.jobId === "string") setActiveJobId(job.jobId);
                    }
                  }}
                >
                  Arrêter
                </button>
              }
              sections={detailSections(row())}
            />
          )}
        </Show>
      </div>
    </PanelLayout>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// VARIANTE B — Push (temps réel via usePanelSubscription)
// ──────────────────────────────────────────────────────────────────────────────
const TemplatePushPanel: Component = () => {
  const [selected, setSelected] = createSignal<DataRow | null>(null);

  // CUSTOMIZE: topic, params, intervalle en ms. `usePanelSubscription`
  // émet `live/panel/subscribe` au montage et `live/panel/unsubscribe`
  // au démontage. `lastUpdate` est bumpé à chaque frame `live/panel/data`.
  const { data, loading, lastUpdate } = usePanelSubscription<DataRow[]>("xxx:list", {}, 3000);

  const panel = usePanel(() => ({ isFetching: loading(), error: null }));

  // RefreshIndicator alimenté par la subscription WS — passe en "live"
  // tant que des frames arrivent, "stale" si silence prolongé, "error"
  // si la WS globale tombe.
  const refresh = usePanelRefresh({
    subscription: { loading, lastUpdate },
  });

  const columns: Column<DataRow>[] = [
    { accessorKey: "name", header: "Nom" },
    {
      accessorKey: "status",
      header: "État",
      cell: (info) => {
        const s = String(info.getValue() ?? "");
        return <PanelBadge intent={statusIntent(s)}>{s || "—"}</PanelBadge>;
      },
    },
  ];

  const toolbar = <RefreshIndicator state={refresh} />;

  return (
    <PanelLayout title="Mon Panel Temps Réel" toolbar={toolbar} status={panel.status()}>
      <DataTable<DataRow>
        rows={data() ?? []}
        columns={columns}
        rowActions={(r) => (
          <button class="btn btn--xs btn--ghost" onClick={() => setSelected(r)}>
            Détails
          </button>
        )}
      />
      <Show when={selected()}>
        {(row) => (
          <PanelDetail
            title={row().name ?? "Détail"}
            onClose={() => setSelected(null)}
            sections={[{ label: "Identité", items: [["Nom", <>{row().name ?? "—"}</>]] }]}
          />
        )}
      </Show>
    </PanelLayout>
  );
};

// NE PAS registerPanel ici — c'est un template
export { TemplatePullPanel, TemplatePushPanel };
